import {
  DuviSharedAdminCard as Card,
  DuviSharedAdminButton as Button,
  DuviSharedAdminInput as Input,
  DuviSharedAdminSelect as Select,
  DuviSharedAdminBadge as Badge,
  DuviSharedAdminIconButton as IconButton,
  DuviSharedAdminIcon as Icon,
  DuviSharedAdminModal as Modal,
  DuviSharedAdminTestIds,
} from "@duvi-agency/ui";
import {
  DuviCoreAdminSkel as Skel,
  DuviCoreAdminEmptyState as EmptyState,
} from "../../components/duvi-core-admin-kit";
import {
  duviCoreAdminToDisplayLabel,
  duviCoreAdminArticlePreview,
  type useDuviCoreAdminKnowledgeBaseList,
} from "./use-duvi-core-admin-knowledge-base-list";

type State = ReturnType<typeof useDuviCoreAdminKnowledgeBaseList>;

export function DuviCoreAdminKnowledgeBaseListView(state: State) {
  const {
    articles,
    isLoading,
    derivedCategories,
    selectedCategory,
    setSelectedCategory,
    searchQuery,
    setSearchQuery,
    deleteArticleId,
    setDeleteArticleId,
    pendingDelete,
    confirmDelete,
    openArticle,
    openNew,
  } = state;

  return (
    <div
      className="flex flex-col gap-5"
      data-testid={DuviSharedAdminTestIds.kbListPage}
    >
      <Card pad>
        <div className="flex gap-3 flex-wrap items-center">
          <Select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="max-w-[190px]"
            data-testid={DuviSharedAdminTestIds.kbFilterCategory}
          >
            <option value="">All categories</option>
            {derivedCategories.map((c) => (
              <option key={c} value={c}>
                {duviCoreAdminToDisplayLabel(c)}
              </option>
            ))}
          </Select>
          <div className="ml-auto max-w-[260px] w-full">
            <Input
              icon={<Icon name="search" size={16} />}
              placeholder="Search articles"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      </Card>

      {isLoading ? (
        <div className="grid grid-cols-3 gap-5 max-[900px]:grid-cols-1">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i} pad>
              <Skel className="h-[18px] w-20 mb-3.5" />
              <Skel className="h-5 mb-2.5" />
              <Skel className="h-[42px] mb-3.5" />
              <Skel className="h-3.5 w-28" />
            </Card>
          ))}
        </div>
      ) : articles.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Icon name="inbox" size={22} />}
            title="No articles yet"
            desc={
              selectedCategory || searchQuery
                ? "No articles match your filters. Try clearing them."
                : "Create your first article to get started."
            }
            testId={DuviSharedAdminTestIds.kbEmpty}
            action={
              <Button
                leftIcon={<Icon name="plus" size={15} />}
                onClick={openNew}
              >
                New article
              </Button>
            }
          />
        </Card>
      ) : (
        <div className="grid grid-cols-3 gap-5 max-[900px]:grid-cols-1">
          {articles.map((a) => (
            <div key={a.article_id} className="group relative">
              <Card
                pad
                data-testid={DuviSharedAdminTestIds.kbRow}
                className="cursor-pointer h-full flex flex-col gap-3"
                onClick={() => openArticle(a.article_id)}
              >
                <div className="flex justify-between items-center">
                  <Badge variant="primary">
                    {duviCoreAdminToDisplayLabel(a.category)}
                  </Badge>
                  <span
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteArticleId(a.article_id);
                    }}
                  >
                    <IconButton
                      label="Delete article"
                      size="sm"
                      data-testid={DuviSharedAdminTestIds.kbActionDelete}
                    >
                      <Icon name="trash" size={16} />
                    </IconButton>
                  </span>
                </div>
                <div className="font-semibold text-h4 tracking-tight">
                  {a.title}
                </div>
                <p className="m-0 text-text-secondary text-body-sm leading-relaxed line-clamp-3">
                  {duviCoreAdminArticlePreview(a.content)}
                </p>
                <div className="flex gap-1.5 flex-wrap">
                  {a.tags.map((t) => (
                    <Badge key={t} variant="neutral">
                      {t}
                    </Badge>
                  ))}
                </div>
                <div className="mt-auto text-text-muted text-caption">
                  Updated {new Date(a.updated_at).toLocaleDateString()}
                </div>
              </Card>
            </div>
          ))}
        </div>
      )}

      {deleteArticleId && (
        // display:contents -- DuviSharedAdminModal renders `position: fixed`,
        // which takes it out of this wrapper's normal flow. Without `contents`
        // the wrapper collapses to 0x0 and Playwright reports the testid
        // "hidden" even though the modal is fully visible (STY-906 triage).
        <div
          className="contents"
          data-testid={DuviSharedAdminTestIds.kbDeleteDialog}
        >
          <Modal
            title="Delete article"
            onClose={() => setDeleteArticleId(null)}
            description={`“${pendingDelete?.title ?? "This article"}” will be permanently removed. This cannot be undone.`}
            footer={
              <>
                <Button
                  variant="secondary"
                  onClick={() => setDeleteArticleId(null)}
                  data-testid={DuviSharedAdminTestIds.kbDeleteDialogCancel}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={confirmDelete}
                  data-testid={DuviSharedAdminTestIds.kbDeleteDialogConfirm}
                >
                  Delete article
                </Button>
              </>
            }
          >
            {pendingDelete && (
              <div className="flex gap-2.5 items-center flex-wrap">
                <Badge variant="primary">
                  {duviCoreAdminToDisplayLabel(pendingDelete.category)}
                </Badge>
                {pendingDelete.tags.map((t) => (
                  <Badge key={t} variant="neutral">
                    {t}
                  </Badge>
                ))}
              </div>
            )}
          </Modal>
        </div>
      )}
    </div>
  );
}
